import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  getSharedDCCTVolumeInfo,
  type FullHeadMPRProjection,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';
import { MedicalImageZoomLayout, RadioGroup } from '@/shared/ui';

import { medicalImageTitles } from '@/entities/assets';
import { reportsModel } from '@/entities/reports';

import type { FullHeadMprReportSelectionResolver } from '@/features/MPR';

import {
  FULL_HEAD_MPR_PROJECTION_TO_SERIES,
  TOOTH_BASIS_SERIES_TO_GROUP,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceTileView,
  type ToothSliceGeometry,
} from '../../config';
import { useToothSliceSettings } from '../../hooks';
import { resolveToothSliceThicknessOptionsMm } from '../../lib';
import { FullHeadMprSliceOptionsControls } from '../FullHeadMprSliceOptionsControls';
import { FullHeadMprSliceSession } from '../FullHeadMprSliceSession';

import styles from './FullHeadMprSliceZoomModal.module.scss';

type FullHeadMprSliceZoomModalProps = {
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  tile: FullHeadMprSliceTileView | null;
  className?: string;
  groups?: FullHeadMprSliceGroupView[];
  onClose: () => void;
  resolveViewSelection: FullHeadMprReportSelectionResolver;
};

export const FullHeadMprSliceZoomModal: FC<FullHeadMprSliceZoomModalProps> = (
  props,
) => {
  const {
    group,
    groups,
    input,
    tile,
    className,
    onClose,
    resolveViewSelection,
  } = props;

  const { formatMessage } = useIntl();

  const [mode, setMode] = useState<'single' | 'three'>('single');
  const [primaryProjection, setPrimaryProjection] =
    useState<FullHeadMPRProjection | null>(null);
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  useEffect(() => {
    setMode('single');
    setPrimaryProjection(null);
  }, [tile?.id]);

  const thicknessOptionsMm = resolveToothSliceThicknessOptionsMm(
    input ? getSharedDCCTVolumeInfo(input.volumeFileID) : null,
  );
  const toothID = tile?.kind === 'view' ? tile.toothID : input?.toothID ?? '';

  const { canChangeOptions, recordsByTileID, sliceOptions, setSeriesOptions } =
    useToothSliceSettings({
      reportID: report?.ID ?? '',
      thicknessOptionsMm,
      toothID,
    });

  if (!tile || (tile.kind !== 'view' && !group)) {
    return null;
  }

  const controlledSeriesName = primaryProjection
    ? FULL_HEAD_MPR_PROJECTION_TO_SERIES[primaryProjection]
    : tile.seriesName;
  const options = sliceOptions[controlledSeriesName];
  const title = formatMessage(
    medicalImageTitles[TOOTH_BASIS_SERIES_TO_GROUP[controlledSeriesName]],
  );

  return (
    <FullHeadMprSliceSession
      className={styles.mprSession}
      group={group}
      groups={groups}
      input={input}
      isSingleProjection={mode === 'single'}
      recordsByTileID={recordsByTileID}
      resolveViewSelection={resolveViewSelection}
      sliceOptions={sliceOptions}
      tile={tile}
      toothID={toothID}
      onPrimaryProjectionChange={setPrimaryProjection}
      renderLayout={({
        children,
        controls,
        shortcutsControl,
        ...layoutProps
      }) => (
        <MedicalImageZoomLayout
          {...layoutProps}
          isOpen
          className={className}
          imageContainerClassName={
            mode === 'three' ? styles.mprImageContainer : undefined
          }
          strideMm={tile.kind === 'view' ? 0 : options.sliceStepMm}
          thicknessControl={
            tile.kind === 'series' && (
              <FullHeadMprSliceOptionsControls
                key={controlledSeriesName}
                disabled={!canChangeOptions || !primaryProjection}
                options={options}
                showStep={false}
                thicknessOptionsMm={thicknessOptionsMm}
                onChange={(nextOptions) =>
                  setSeriesOptions(controlledSeriesName, nextOptions)
                }
              />
            )
          }
          thicknessMm={
            tile.kind === 'view' ? tile.view.thicknessMm : options.thicknessMm
          }
          title={title}
          onClose={onClose}
          controls={
            <div className={styles.controls}>
              <RadioGroup
                variant="tab"
                name="fullHeadMprSliceMode"
                value={mode}
                items={[
                  {
                    value: 'single',
                    label: (
                      <FormattedMessage
                        id="global.slice"
                        defaultMessage="Slice"
                      />
                    ),
                  },
                  {
                    value: 'three',
                    label: (
                      <FormattedMessage
                        id="medicalImageTitle.savedMPR"
                        defaultMessage="MPR"
                      />
                    ),
                  },
                ]}
                onChange={({ target: { value } }) => {
                  if (value === 'single' || value === 'three') {
                    setMode(value);
                  }
                }}
              />
              {controls && (
                <div className={styles.viewControls}>{controls}</div>
              )}
              {shortcutsControl}
            </div>
          }
        >
          {children}
        </MedicalImageZoomLayout>
      )}
    />
  );
};
